import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { NAV_CONST } from '@/src/constants/nav';
import {
  DeleteDonationByIdAPI,
  getDonationByIdAPI,
} from '@/src/apis/rest/donation';
import dayjs from 'dayjs';
import DonationPreview from '@/src/pages/Donation/components/DonationPreview';
import { webviewDataInjection } from '@/src/helpers/webviewHelper';
import DetailSection from '@/src/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DonationParticipantsModal from '@/src/pages/Donation/components/DonationParticipantsModal';
import useModalState from '@/src/hooks/useModalState';
import {
  DonationWebviewTemplate,
  donationWebviewMapper,
} from '@/src/helpers/webviewHelper/donation';
import { getContentTemplatesAPI } from '@/src/apis/rest/contentTemplate';
import ContentsPreview from '@/src/components/molecule/ContentsPreview';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoSectionCard, {
  InfoData,
} from '@/components/organism/InfoSectionCard/InfoSectionCard';
import { UNIT_STRINGS } from '@/src/constants/unit';
import SectionCard from '@/src/components/molecule/SectionCard';
import AlertDialog from '@/src/components/atom/AlertDialog';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { APIError } from '@/src/models/common/apiError';
import { inAppLinkRegex } from '@/src/utils/regex';
import Typo from '@/src/components/atom/Typo';
import DonationDto from '@/src/models/DonationDto';

type ModalState = 'NONE' | 'HISTORIES' | 'DELETE' | 'BANNER_CONNECT_ERROR';

const LIST_PATH = PATH.DONATION_LIST;
const BACK_PATH = PATH.DONATION_LIST;

export default function DonationDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: detailData } = useQuery({
    queryKey: ['donation-detail', id],
    queryFn: () => getDonationByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });
  const { data: contentTemplateData } = useQuery({
    queryKey: ['content-template', 'DONATION'],
    queryFn: () => {
      return getContentTemplatesAPI({ type: 'DONATION' });
    },
  });

  // mutation
  const deleteMutation = useMutation({
    mutationFn: DeleteDonationByIdAPI,
  });

  // state
  const contentsMarkup = webviewDataInjection<DonationWebviewTemplate>(
    contentTemplateData?.rows?.[0]?.markup || '',
    donationWebviewMapper(detailData),
  );
  const { modalState, handleToggleModal, handleOpenModal, handleCloseModal } =
    useModalState<ModalState>();

  const hasOptionalInfo =
    !!detailData?.bottomButtonDisplayYn ||
    !!detailData?.sponsorImage ||
    !!detailData?.sponsorDescription ||
    !!detailData?.sponsorLink ||
    false;

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDeleteMagazine = () => {
    deleteMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });
          setTimeout(() => {
            navigate(LIST_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'THIS_CONTENT_HAVE_BANNER') {
            handleOpenModal('BANNER_CONNECT_ERROR');
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex gap-3">
            <Button
              className="w-40"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => handleOpenModal('DELETE')}
              isLoading={deleteMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.delete}
              </div>
            </Button>
            <Link to={PATH.DONATION_EDIT.replace(':id', String(id))}>
              <Button className="w-40" buttonSize="medium">
                <div className="flex items-center gap-x-2">
                  {BUTTON_CONST.edit}
                </div>
              </Button>
            </Link>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.donationManagement,
          NAV_CONST.donationDetail,
        ]}
        pageTitle={NAV_CONST.donationDetail}
      />
      {detailData && (
        <div className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4">
          <DetailSection
            className="col-span-2"
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  기부하기 기본 정보
                </span>
              </div>
            }
            data={detailData}
            contents={[
              [
                {
                  key: 'startedAt',
                  lable: '모금 기간',
                  valueSetter: data =>
                    `${dayjs(data?.startedAt).format(
                      'YYYY-MM-DD',
                    )} ~ ${dayjs(data?.endedAt).format('YYYY-MM-DD')}`,
                },
                {
                  key: 'isExposed',
                  lable: '노출 여부',
                  valueSetter: data => (data?.isExposed ? 'true' : 'false'),
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.exposed,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notExposed,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'priorityDisplayYn',
                  lable: '우선 노출 여부',
                  valueSetter: data =>
                    data?.priorityDisplayYn ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.applied,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notApplied,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'thumbnailImage',
                  lable: '썸네일 이미지',
                  valueSetter: data =>
                    data?.thumbnailImage?.bufferFullUrl || '',
                  render: DetailSctionRenderer.IMAGE({
                    width: 128,
                    height: 128,
                  }),
                },
              ],
              [
                {
                  key: 'allowDuplicateParticipant',
                  lable: '참여 방법',
                  valueSetter: (data: DonationDto) =>
                    data?.allowDuplicateParticipant ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.allowDuplicateParticipant,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notAllowDuplicateParticipant,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'participantsCnt',
                  lable: '참여자 수',
                  render: value => {
                    return (
                      <div className="flex gap-2">
                        <p className="text-b16r text-gray6">
                          {(!isNaN(Number(value))
                            ? Number(value)
                            : 0
                          ).toLocaleString()}
                          명
                        </p>
                        {!isNaN(Number(value)) && Number(value) > 0 && (
                          <Button
                            buttonSize="xSmall"
                            buttonColor="secondary"
                            onClick={() => handleOpenModal('HISTORIES')}
                          >
                            참여한 회원 목록 보기
                          </Button>
                        )}
                      </div>
                    );
                  },
                },
                {
                  key: 'participantsCredit',
                  lable: '모금 크레딧',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.participantsCredit === 'number'
                        ? String(data?.participantsCredit)
                        : '0',
                    );
                  },
                  render: value => `${value}${UNIT_STRINGS.CREDIT}`,
                },
                {
                  key: 'goalCredit',
                  lable: '목표 크레딧',
                  valueSetter: data => {
                    return DetailSctionRenderer.NUMBER()(
                      typeof data?.goalCredit === 'number'
                        ? String(data?.goalCredit)
                        : '0',
                    );
                  },
                  render: value => `${value}${UNIT_STRINGS.CREDIT}`,
                },
                {
                  key: 'createdAt',
                  lable: '등록 일시',
                  render: DetailSctionRenderer.DATE({
                    format: 'YYYY-MM-DD HH:mm:ss',
                  }),
                },
                {
                  key: ['administrator', 'name'],
                  lable: '등록 관리자',
                },
              ],
            ]}
          />
          <DetailSection
            className="col-span-2"
            isCollapsible
            defaultOpen={hasOptionalInfo}
            sectionTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  기부하기 선택 정보
                </span>
              </div>
            }
            data={detailData}
            contents={[
              [
                {
                  key: 'sponsorImage',
                  lable: '제휴사 이미지',
                  valueSetter: data => data?.sponsorImage?.bufferFullUrl || '',
                  render: DetailSctionRenderer.IMAGE({
                    width: 128,
                    height: 128,
                  }),
                },
                {
                  key: 'sponsorDescription',
                  lable: '제휴사 설명',
                },
                {
                  key: 'sponsorLink',
                  lable: '제휴사 홈페이지 링크',
                  valueSetter: data => {
                    return data?.sponsorLink || '';
                  },
                  render: (data: string) =>
                    data ? (
                      <Link
                        to={data}
                        target="_blank"
                        className="break-all text-b16r text-gray6 underline"
                      >
                        {data}
                      </Link>
                    ) : (
                      '-'
                    ),
                },
              ],
              [
                {
                  key: 'bottomButtonDisplayYn',
                  lable: '하단 버튼 노출 여부',
                  valueSetter: data =>
                    data?.bottomButtonDisplayYn ? 'true' : 'false',
                  render: DetailSctionRenderer.BADGE({
                    options: [
                      {
                        label: OPTIONS_CONST.displayed,
                        key: 'true',
                        badgeColor: 'lightGreen',
                      },
                      {
                        label: OPTIONS_CONST.notDisplayed,
                        key: 'false',
                        badgeColor: 'lightGray',
                      },
                    ],
                  }),
                },
                {
                  key: 'bottomButtonName',
                  lable: '하단 버튼 명',
                },
                {
                  key: 'bottomButtonLink',
                  lable: '하단 버튼 링크',
                  valueSetter: data => {
                    return data?.bottomButtonLink || '';
                  },
                  render: (data: string) =>
                    data ? (
                      inAppLinkRegex.test(data) ? (
                        <Typo typoType="b16r" typoColor="gray6">
                          {data}
                        </Typo>
                      ) : (
                        <Link
                          to={data}
                          target="_blank"
                          className="break-all text-b16r text-gray6 underline"
                        >
                          {data}
                        </Link>
                      )
                    ) : (
                      '-'
                    ),
                },
              ],
            ]}
          />
          <SectionCard
            className="col-span-2"
            cardTitle={
              <div className="flex w-full items-center justify-between">
                <span className="font-pretendardSemiBold text-b18sb">
                  기부하기 내용
                </span>
              </div>
            }
          >
            <div className="flex gap-6">
              <ContentsPreview
                title={detailData?.title}
                subTitle={detailData?.subTitle}
                contents={detailData?.contents}
              />
              <DonationPreview contentsMarkup={contentsMarkup} />
            </div>
          </SectionCard>
          <InfoSectionCard
            cardTitle="안내사항 영역"
            infoData={(detailData?.donationInfos as InfoData[]) || []}
          />
          <InfoSectionCard
            cardTitle="유의사항 영역"
            infoData={(detailData?.donationCautions as InfoData[]) || []}
          />
          {renderBottomFixedContent()}
        </div>
      )}

      {/* 모달 */}
      {modalState === 'HISTORIES' && (
        <DonationParticipantsModal
          open={modalState === 'HISTORIES'}
          onOpenChange={() => handleToggleModal('HISTORIES')}
          id={Number(id)}
          contentStartedAt={detailData?.startedAt}
          contentEndedAt={detailData?.endedAt}
        />
      )}
      {modalState === 'DELETE' && (
        <AlertDialog
          open={modalState === 'DELETE'}
          onOpenChange={() => handleToggleModal('DELETE')}
          okText={BUTTON_CONST.ok}
          onOk={handleDeleteMagazine}
          title="기부하기 삭제"
          okButtonProps={{
            isLoading: deleteMutation.isPending,
          }}
        >
          <p className="whitespace-pre-wrap">
            기부하기를 삭제하시겠습니까?
            <br />
            삭제된 기부하기는 사용자에게 노출되지 않습니다.
          </p>
        </AlertDialog>
      )}
      {modalState === 'BANNER_CONNECT_ERROR' && (
        <AlertDialog
          open={modalState === 'BANNER_CONNECT_ERROR'}
          onOpenChange={() => handleToggleModal('BANNER_CONNECT_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="삭제 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            연결된 배너가 있어서 삭제가 불가능합니다.
            <br />
            배너를 확인해주세요.
          </p>
        </AlertDialog>
      )}
    </>
  );
}
